import {
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button
} from '@mui/material'
import React, { useCallback } from 'react'
import PhoneInput from 'react-phone-input-2'
import { ErrorAlert } from '../formik/ErrorAlert'
import { UserState } from '../state/user'
import { useFormik } from 'formik'
import { RefreshActions } from '../state/refresh'
import { validateMail, validatePhone } from '../utils/validators'
import { useDispatch, useSelector } from 'react-redux'
import { useRequests } from '../requests/useRequests'
import { State } from '../types/state'
import { useIsMobileView } from '../hooks/useIsMobileView'

const UserDetails = (): JSX.Element => {
  const dispatch = useDispatch()
  const isMobileView = useIsMobileView()
  const requestMaker = useRequests()
  const userState = useSelector((state: State) => state.user)

  const validate = (user: Partial<UserState>): Partial<UserState> => {
    const errors: typeof user = {}

    validateMail(user.email ?? null, e => (errors.email = e))
    validatePhone(user.phoneNumber ?? null, e => (errors.phoneNumber = e))

    return errors
  }

  const onSubmit = useCallback(
    async (user: UserState): Promise<void> => {
      await requestMaker.updateUserDetails({
        username: user.username!,
        email: user.email!,
        phoneNumber: user.phoneNumber!
      })
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Succesfully updated user details'
        })
      )
      dispatch(RefreshActions.refreshUserState())
    },
    [requestMaker, dispatch]
  )

  const formik = useFormik({
    initialValues: userState,
    onSubmit,
    enableReinitialize: true,
    validate
  })

  const handleChangeUsername = async (username: string): Promise<void> => {
    void formik.setFieldValue('username', username)
    if (username === userState.username) {
      return
    }

    const response = await requestMaker.emailOrUsernameExists(username)

    if (response.data) {
      void formik.setFieldError('username', 'User with this username exists')
    } else {
      void formik.setFieldError('username', undefined)
    }
    void formik.setFieldError('username', undefined)
  }

  const handlePhoneNumberBlur = async (phoneNumber: string): Promise<void> => {
    if (phoneNumber === userState.phoneNumber) {
      return
    }

    const response = await requestMaker.phoneNumberExists(phoneNumber)

    if (response.data) {
      void formik.setFieldError('phoneNumber', 'Phone number exists')
    } else {
      void formik.setFieldError('phoneNumber', undefined)
    }
  }

  const handleEmailBlur = async (email: string): Promise<void> => {
    if (email === userState.email) {
      return
    }

    const response = await requestMaker.emailExists(email)

    if (response.data) {
      void formik.setFieldError('email', 'Email exists')
    } else {
      void formik.setFieldError('email', undefined)
    }
  }

  return (
    <form>
      <div style={{ width: isMobileView ? '80vw' : '40vw', margin: 'auto' }}>
        <DialogTitle>User details</DialogTitle>
        <DialogContent>
          <TextField
            error={formik.errors.username !== undefined}
            variant='filled'
            helperText={formik.errors.username}
            value={formik.values.username}
            onChange={e => {
              void handleChangeUsername(e.target.value)
            }}
            onBlur={e => {
              void handleChangeUsername(e.target.value)
            }}
            margin='normal'
            required
            fullWidth
            id='username'
            label='Username'
            name='username'
            autoComplete='username'
            autoFocus
          />
          <TextField
            margin='dense'
            id='email'
            label='Email'
            type='email'
            fullWidth
            variant='standard'
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={e => {
              void handleEmailBlur(e.target.value)
            }}
            style={{ marginBottom: '3vh' }}
          />
          <ErrorAlert formik={formik} name='email' />
          <div
            style={{ width: isMobileView ? '60vw' : '20vw', margin: 'auto' }}
          >
            <PhoneInput
              placeholder='Enter phone number'
              inputClass='material'
              containerClass='bg-white focus:outline-none focus:shadow-outline border border-gray-300 rounded-lg py-2 px-4 block w-full appearance-none leading-normal'
              value={formik.values.phoneNumber}
              onChange={phoneNumber => {
                void formik.setFieldValue('phoneNumber', phoneNumber)
              }}
              onBlur={e => {
                void handlePhoneNumberBlur(e.target.value)
              }}
            />
            <ErrorAlert formik={formik} name='phoneNumber' />
          </div>
        </DialogContent>
      </div>

      <DialogActions>
        <Button
          onClick={formik.handleSubmit as any}
          style={{ margin: 'auto' }}
          variant='contained'
        >
          Update
        </Button>
      </DialogActions>
    </form>
  )
}

export default UserDetails
